import { api, ResponseMap } from '@/lib/utils/api.utils';
import { ERROR_CODE } from '@/lib/config/errorCode';
import { SERVER_CODE } from '@/lib/config/serverCode';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { toaster } from '@/components/ui/toaster';
import { useSession } from '@/lib/hooks/useSession';
import { useInfiniteQuery } from '@tanstack/react-query';
import { type OrderStatus } from '@/components/ui/orderResultToaster';

export enum SwapType {
  BUY = 1,
  SELL = 2,
}

type MarketOrderPayload = {
  chainId: number;
  swapType: SwapType;
  amountIn: string;
  tokenCA: string;
  doubleOut?: boolean;
  isOneClick?: boolean;
};

type MarketOrderResponse = ResponseMap<{
  txHash: string;
}>;

const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

export async function postMarketOrder(payload: MarketOrderPayload) {
  const { chainId, swapType, amountIn, tokenCA, doubleOut, isOneClick } =
    payload;
  const response = await api.post(
    `${requestUrl}/v1/trade/create_market_order`,
    {
      json: {
        chain_id: chainId,
        swap_type: swapType,
        amount_in: amountIn,
        token_ca: tokenCA,
        double_out: doubleOut,
        is_one_click: isOneClick,
      },
      timeout: 30000,
    },
  );

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<MarketOrderResponse>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
  return data.data;
}

export interface LimitOrderPayload {
  amount: string;
  chainId: number;
  doubleOut?: boolean;
  priceUsd?: string;
  swapType: number;
  tokenCa: string;
  tokenCap?: string;
}

export interface TrailingOrderPayload {
  amount: string;
  chainId: number;
  trailingPercent: number;
  tokenCa: string;
}

export type LimitOrderResponse = ResponseMap<{
  orderId: string;
}>;

export type TrailingOrderResponse = ResponseMap<{
  orderId: string;
}>;

export async function postLimitOrder(
  params: LimitOrderPayload,
): Promise<LimitOrderResponse | undefined> {
  const response = await api.post(
    `${requestUrl}/v1/trade/create_limit_order/`,
    {
      json: {
        chain_id: params.chainId,
        swap_type: params.swapType,
        amount: params.amount,
        token_ca: params.tokenCa,
        price_usd: params.priceUsd,
        double_out: params.doubleOut,
        token_cap: params.tokenCap,
      },
      timeout: 30000,
    },
  );

  if (!response.ok) {
    throw new Error('network error');
  }

  const data = await response.json<LimitOrderResponse>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data;
}

export async function postTrailingOrder(
  params: TrailingOrderPayload,
): Promise<TrailingOrderResponse | undefined> {
  const response = await api.post(
    `${requestUrl}/v1/trade/create_trailing_stop`,
    {
      json: {
        chain_id: params.chainId,
        amount: params.amount,
        token_ca: params.tokenCa,
        trailing_percent: params.trailingPercent,
      },
      timeout: 30000,
    },
  );

  if (!response.ok) {
    throw new Error('network error');
  }

  const data = await response.json<TrailingOrderResponse>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data;
}

export enum TradeType {
  ALL = 0,
  MARKET = 1,
  LIMIT = 2,
  ONE_CLICK = 3,
  TOKEN_CAP_LIMIT = 4,
  TRAILING_STOP = 5,
}

export interface QueryCurrentOrderPayload {
  pageNo: number;
  pageSize: number;
  swapType?: SwapType;
  tokenCa?: string;
  tradeType?: TradeType;
}

export interface OrderItem {
  amount: string;
  cap: string;
  chainId: string;
  createTime: string;
  id: string;
  price: string;
  priceUsd: string;
  status: string;
  swapType: string;
  tokenCa: string;
  tokenIcon: string;
  tokenSymbol: string;
  tradeType: string;
  updateTime: string;
  value: string;
  valueUsd: string;
}

export type QueryCurrentOrderResponse = {
  list: OrderItem[];
  total: string;
};

export async function queryCurrentOrder(
  params: QueryCurrentOrderPayload,
): Promise<QueryCurrentOrderResponse | undefined> {
  const queryString = new URLSearchParams({
    page_no: params.pageNo.toString(),
    page_size: params.pageSize.toString(),
    ...(params.swapType && { swap_type: params.swapType.toString() }),
    ...(params.tokenCa && { token_ca: params.tokenCa }),
    ...(params.tradeType && { trade_type: params.tradeType.toString() }),
  }).toString();

  const response = await api.get(
    `${requestUrl}/v1/trade/query_current_order?${queryString}`,
    {
      timeout: 30000,
    },
  );

  if (!response.ok) {
    throw new Error('network error');
  }

  const data = await response.json<ResponseMap<QueryCurrentOrderResponse>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
  return data.data;
}

export function useQueryCurrentOrder(params: QueryCurrentOrderPayload) {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['currentOrders', params],
    queryFn: () => queryCurrentOrder(params),
    select: (data) => {
      if (!data) {
        return {
          list: [],
          total: '0',
        };
      }
      return data;
    },
    enabled: !!session?.isLogin,
    staleTime: 1000 * 60, // 数据1分钟内认为是新鲜的
    retry: 2, // 失败时重试2次
  });
}

export function useInfiniteCurrentOrder(
  params: Omit<QueryCurrentOrderPayload, 'pageNo'>,
) {
  const { data: session } = useSession();
  return useInfiniteQuery({
    queryKey: ['infinite_current_order', params],
    queryFn: ({ pageParam = 1 }) =>
      queryCurrentOrder({
        ...params,
        pageNo: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.list.length < params.pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: !!session?.isLogin,
  });
}

export interface QueryTradeHistoryPayload {
  pageNo: number;
  pageSize: number;
  tokenCa?: string;
  orderFieldName?: '0' | '1' | '2' | '3' | '4'; // all, cap, price, amount, value
  orderIsAsc?: boolean;
  swapType?: 0 | 1 | 2; // all, buy, sell
  tradeType?: 0 | 1 | 2 | 3 | 4 | 5; // all, market, limit, one_click, token_cap_limit, trailing_stop
}

export interface TradeHistoryItem {
  id: string;
  amount: string;
  cap: string;
  chainId: string;
  createTime: string;
  price: string;
  priceUsd: string;
  swapType: SwapType;
  tokenCa: string;
  tokenIcon: string;
  tokenSymbol: string;
  tradeType: string;
  txHash: string;
  updateTime: string;
  value: string;
  valueUsd: string;
}

export interface OrderHistoryResponse {
  list: OrderHistoryInfo[];
}

export interface OrderHistoryInfo extends Omit<TradeHistoryItem, 'txHash'> {
  status: OrderStatus;
  txHash?: string;
}

export interface TradeHistoryResponse {
  list: TradeHistoryItem[];
}

export async function requestTradeHistory(
  params: QueryTradeHistoryPayload,
): Promise<TradeHistoryResponse | undefined> {
  const searchParams: Record<string, string> = {
    page_no: params.pageNo.toString(),
    page_size: params.pageSize.toString(),
  };

  if (params.tokenCa) {
    searchParams.token_ca = params.tokenCa;
  }
  if (params.orderFieldName) {
    searchParams.order_field_name = params.orderFieldName;
  }
  if (params.orderIsAsc !== undefined) {
    searchParams.order_is_asc = params.orderIsAsc.toString();
  }
  if (params.swapType !== undefined) {
    searchParams.swap_type = params.swapType.toString();
  }
  if (params.tradeType !== undefined) {
    searchParams.trade_type = params.tradeType.toString();
  }

  const response = await api.get(`${requestUrl}/v1/trade/query_trade_history`, {
    searchParams,
    timeout: 20000,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<TradeHistoryResponse>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export async function requestOrderHistory(
  params: QueryTradeHistoryPayload,
): Promise<OrderHistoryResponse | undefined> {
  const searchParams: Record<string, string> = {
    page_no: params.pageNo.toString(),
    page_size: params.pageSize.toString(),
  };

  if (params.tokenCa) {
    searchParams.token_ca = params.tokenCa;
  }
  if (params.orderFieldName) {
    searchParams.order_field_name = params.orderFieldName;
  }
  if (params.orderIsAsc !== undefined) {
    searchParams.order_is_asc = params.orderIsAsc.toString();
  }
  if (params.swapType !== undefined) {
    searchParams.swap_type = params.swapType.toString();
  }
  if (params.tradeType !== undefined) {
    searchParams.trade_type = params.tradeType.toString();
  }
  const response = await api.get(`${requestUrl}/v1/trade/query_order_history`, {
    searchParams,
    timeout: 20000,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<OrderHistoryResponse>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export function useTradeHistory(
  params: QueryTradeHistoryPayload,
): UseQueryResult<TradeHistoryResponse, Error> {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['trade_history', params],
    queryFn: () => requestTradeHistory(params),
    enabled: !!session?.isLogin,
  });
}

export function useOrderHistory(
  params: QueryTradeHistoryPayload,
): UseQueryResult<OrderHistoryResponse, Error> {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['order_history', params],
    queryFn: () => requestOrderHistory(params),
    enabled: !!session?.isLogin,
  });
}

export function useInfiniteOrderHistory(
  params: Omit<QueryTradeHistoryPayload, 'pageNo'>,
) {
  const { data: session } = useSession();
  return useInfiniteQuery({
    queryKey: ['infinite_order_history', params],
    queryFn: ({ pageParam = 1 }) =>
      requestOrderHistory({
        ...params,
        pageNo: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.list.length < params.pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: !!session?.isLogin,
  });
}
export function useInfiniteTradeHistory(
  params: Omit<QueryTradeHistoryPayload, 'pageNo'>,
) {
  const { data: session } = useSession();
  return useInfiniteQuery({
    queryKey: ['infinite_trade_history', params],
    queryFn: ({ pageParam = 1 }) =>
      requestTradeHistory({
        ...params,
        pageNo: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.list.length < params.pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: !!session?.isLogin,
  });
}

export interface Holding {
  baseStandard: BaseStandard;
  baseStandardToUsd: BaseStandard;
  buyTokenAmount: string;
  holdTokenAmount: string;
  sellTokenAmount: string;
  tokeInfo: HoldingTokenInfo;
}

export interface BaseStandard {
  buyTokenAverageFdv: string;
  buyTokenAveragePrice: string;
  buyTokenValue: string;
  holdTokenValue: string;
  realizedTokenProfit: string;
  sellTokenAveragePrice: string;
  sellTokenValue: string;
  totalTokenProfit: string;
  totalTokenProfitYield: string;
  unrealizedTokenProfit: string;
}

export interface HoldingTokenInfo {
  basePrice: string;
  chainId: string;
  change24h: string;
  tokenCa: string;
  tokenIcon: string;
  tokenName: string;
  tokenPrice: string;
  tokenPriceUsd: string;
  tokenSupply: string;
  tokenSymbol: string;
}

export interface HoldingQuery {
  chain_id?: number;
}

export interface HoldingHistoryResponse {
  list: Holding[];
}

export async function requestHoldingHistory(
  params: HoldingQuery,
): Promise<HoldingHistoryResponse | undefined> {
  const response = await api.get(`${requestUrl}/v1/trade/query_my_holding`, {
    searchParams: {
      chain_id: params?.chain_id ?? '',
    },
    timeout: 20000,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<HoldingHistoryResponse>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export function useHoldingHistory(
  params: HoldingQuery,
): UseQueryResult<HoldingHistoryResponse, Error> {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['holding_history', params],
    queryFn: () => requestHoldingHistory(params),
    enabled: !!session?.isLogin,
  });
}

export type GasItem = {
  chainId: string;
  fast: string;
  fastUsd: string;
  fastPrice: string;
  normal: string;
  normalUsd: string;
  normalPrice: string;
  superFast: string;
  superFastUsd: string;
  superFastPrice: string;
};

export type QueryGasInfoResponse = {
  gasInfo: GasItem[];
};

export async function queryGasInfo(
  chainId: number,
): Promise<QueryGasInfoResponse | undefined> {
  try {
    const response = await api.get(
      `${requestUrl}/v1/trade/get_gas_info?chain_id=${chainId}`,
      {
        timeout: 30000,
      },
    );

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }

    const data = await response.json<ResponseMap<QueryGasInfoResponse>>();
    if (data.code !== SERVER_CODE.SUCCESS) {
      throw new Error(data.message);
    }
    return data.data;
  } catch (error: any) {
    // toaster.create({
    //   title: error.message,
    //   type: 'error',
    // });
  }
}

export function useGasInfo(chainId: number) {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['gasInfo', chainId],
    queryFn: () => queryGasInfo(chainId),
    staleTime: 1000 * 60, // 数据1分钟内认为是新鲜的
    retry: 2, // 失败时重试2次
    enabled: !!session?.isLogin,
  });
}

export interface CancelOrderPayload {
  order_id: string;
}

export async function cancelOrder(
  params: CancelOrderPayload,
): Promise<boolean> {
  const response = await api.post(`${requestUrl}/v1/trade/cancel_order/`, {
    json: params,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<boolean>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
  return true;
}

export type TradeMarketOrderResponse = {
  txHash: string;
};

export type TradeMarketOrderParams = {
  chain_id: string | number;
  token_ca: string;
  amount_in: string;
  swap_type: number;
  is_one_click: boolean;
  double_out: boolean;
};

export async function fetchCreateTradeMarketOrder(
  params: TradeMarketOrderParams,
): Promise<TradeMarketOrderResponse | undefined> {
  try {
    const response = await api.post(
      `${requestUrl}/v1/trade/create_market_order`,
      {
        json: params,
      },
    );

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }

    const data = await response.json<ResponseMap<TradeMarketOrderResponse>>();
    if (!data.data) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }
    if (data.code != 10000) {
      toaster.create({
        title: data.message,
        type: 'error',
      });
    }
    return data.data;
  } catch (e: any) {
    toaster.create({
      title: e.message,
      type: 'error',
    });
  }
}
